import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreatedFolder, DownloadUrl, UploadTicket, UploadedAsset } from '@sparkery/contracts';
import { ApiError, apiDelete, apiGet, apiPost } from './api.ts';

/**
 * The asset writes, from the browser's side.
 *
 * The one that is not like the others is the upload: **the file does not go through our
 * API at all.** Three steps — ask for a ticket, POST the bytes straight to S3, tell the
 * API it landed — and the middle one is a cross-origin request to a bucket that knows
 * nothing about this app's session. So it is written with `XMLHttpRequest` rather than
 * `fetch`, for two reasons that both matter:
 *
 * - **Progress.** A 200MB brand video needs a progress bar, and `fetch` has no upload
 *   progress event. Without one the page looks hung for minutes and people retry.
 * - **No credentials.** None of our headers may go to the bucket, and the request must
 *   not carry cookies. The signed policy is the whole authorisation.
 */

export interface UploadProgress {
  /** The file being sent, for the label. */
  filename: string;
  /** 0–1, or null before the first progress event arrives. */
  fraction: number | null;
  phase: 'signing' | 'uploading' | 'recording' | 'done' | 'failed';
  error?: string;
}

/**
 * POSTs one file to S3 with a presigned ticket.
 *
 * The field order is not arbitrary: S3 reads the form sequentially and ignores anything
 * after the `file` part, so the policy fields have to come first. Appending `file` last
 * is a requirement, not a style choice.
 */
function postToBucket(
  ticket: UploadTicket,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [name, value] of Object.entries(ticket.fields)) form.append(name, value);
    form.append('file', file);

    const request = new XMLHttpRequest();
    request.open('POST', ticket.url, true);

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    });

    request.addEventListener('load', () => {
      // S3 answers a browser POST with 204 by default, or 201 when the policy asks for
      // one. Anything else is the policy refusing — usually the size condition.
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(
        new Error(
          request.status === 403
            ? 'The storage service refused the upload — it may be too large, or the ticket may have expired.'
            : `The upload failed (${request.status}).`,
        ),
      );
    });

    request.addEventListener('error', () =>
      // No status at all: DNS, TLS, or the bucket's CORS rule not allowing this origin.
      // Worth naming, because CORS is the usual cause on a fresh environment.
      reject(new Error('Could not reach the storage service. Check the bucket CORS rules.')),
    );
    request.addEventListener('abort', () => reject(new Error('Upload cancelled.')));

    request.send(form);
  });
}

/**
 * The whole three-step upload, one file at a time.
 *
 * Sequential rather than parallel: a folder of twenty logos uploaded at once competes
 * with itself for bandwidth and makes every progress bar useless. One at a time is
 * slower in theory and clearer in practice.
 */
export function useAssetUpload(folderId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<UploadProgress[]>([]);
  const [busy, setBusy] = useState(false);

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setBusy(true);
      setProgress(
        files.map((file) => ({ filename: file.name, fraction: null, phase: 'signing' as const })),
      );

      const update = (index: number, patch: Partial<UploadProgress>) =>
        setProgress((current) =>
          current.map((row, at) => (at === index ? { ...row, ...patch } : row)),
        );

      for (const [index, file] of files.entries()) {
        try {
          const ticket = await apiPost<UploadTicket>(`/assets/folders/${folderId}/uploads`, {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            sizeBytes: file.size,
          });

          update(index, { phase: 'uploading', fraction: 0 });
          await postToBucket(ticket, file, (fraction) => update(index, { fraction }));

          // Until this lands the object exists but is not an asset. A failure here is
          // the one orphan case the design accepts, and the message says so.
          update(index, { phase: 'recording', fraction: 1 });
          await apiPost<UploadedAsset>(`/assets/folders/${folderId}/assets`, { key: ticket.key });

          update(index, { phase: 'done', fraction: 1 });
        } catch (error) {
          update(index, {
            phase: 'failed',
            error:
              error instanceof ApiError || error instanceof Error
                ? error.message
                : 'The upload failed.',
          });
        }
      }

      setBusy(false);
      // One invalidation at the end, not per file: the listing is a single query, and
      // refetching it twenty times would be twenty renders of the same table.
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
    [folderId, queryClient],
  );

  return {
    upload,
    busy,
    progress,
    clear: () => setProgress([]),
  };
}

export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; isPublic: boolean }) =>
      apiPost<CreatedFolder>('/assets/folders', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (folderId: string) =>
      apiDelete<{ objectsDeleted: number; objectsOrphaned: number }>(`/assets/folders/${folderId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assetId: string) => apiDelete<{ orphaned: boolean }>(`/assets/${assetId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
  });
}

export function useGrantFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { folderId: string; partnerId: string }) =>
      apiPost<{ created: boolean }>(`/assets/folders/${input.folderId}/grants`, {
        partnerId: input.partnerId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
  });
}

export function useRevokeFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { folderId: string; partnerId: string }) =>
      apiDelete<{ removed: boolean }>(
        `/assets/folders/${input.folderId}/grants/${input.partnerId}`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assets'] }),
  });
}

/**
 * Downloads one asset.
 *
 * The URL is fetched on the click rather than rendered into the table, because a signed
 * URL needs no credentials: a page holding one per row would be a page of shareable
 * links to private files, live for as long as the tab is open.
 *
 * The object is served with `Content-Disposition: attachment`, so assigning
 * `location.href` starts a download without navigating away from the portal.
 */
export function useAssetDownload() {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const download = useCallback(async (assetId: string) => {
    setPending(assetId);
    setError(null);
    try {
      const signed = await apiGet<DownloadUrl>(`/assets/${assetId}/download`);
      window.location.href = signed.url;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not start the download.');
    } finally {
      setPending(null);
    }
  }, []);

  return { download, pending, error };
}
